/**
 * Dropdown component - A simple dropdown menu
 */

import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useClickOutside } from '../../hooks/useClickOutside';

interface DropdownOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface DropdownProps {
  trigger: React.ReactNode;
  options: DropdownOption[];
  onSelect: (value: string | number) => void;
  align?: 'left' | 'right';
  className?: string;
}

export default function Dropdown({ trigger, options, onSelect, align = 'right', className }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLDivElement>(null);

  useClickOutside(triggerRef, () => setIsOpen(false), isOpen);

  const handleOpen = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const margin = 8;
      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      // 下方空間不夠、上方空間比較充裕時，改成往上展開，避免選單被畫面下緣切掉
      const openUpward = spaceBelow < 200 && spaceAbove > spaceBelow;

      setMenuStyle({
        position: 'fixed',
        ...(openUpward
          ? { bottom: window.innerHeight - rect.top + margin, maxHeight: spaceAbove }
          : { top: rect.bottom + margin, maxHeight: spaceBelow }),
        ...(align === 'right' ? { right: window.innerWidth - rect.right } : { left: rect.left }),
        overflowY: 'auto',
        zIndex: 9999,
      });
    }
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (value: string | number, disabled?: boolean) => {
    if (disabled) return;
    onSelect(value);
    setIsOpen(false);
  };

  return (
    <div className={`relative${className ? ` ${className}` : ''}`} ref={triggerRef}>
      <div className="flex flex-1 h-full" onClick={handleOpen}>
        {trigger}
      </div>

      {isOpen && createPortal(
        <div
          className="w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1"
          style={menuStyle}
        >
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => handleSelect(option.value, option.disabled)}
              disabled={option.disabled}
              className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                option.disabled
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-100 cursor-pointer'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
